import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, ExternalLink, RefreshCw, Star, Loader2 } from 'lucide-react';
import WishlistStar from '../../WishlistStar';

const PublicMarketplaceSection = ({ currentUser }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSubmitted, setSearchSubmitted] = useState(false);
  const [activeSource, setActiveSource] = useState('all'); // 'all', 'reverb', 'ebay'
  const [filters, setFilters] = useState({
    category: '',
    condition: '',
    minPrice: '',
    maxPrice: '',
    sort: 'relevance'
  });
  const [error, setError] = useState(null);
  
  // Refs to track previous values
  const prevSourceRef = useRef(activeSource);
  const prevFiltersRef = useRef(filters);

  // Core fetch function
  const doFetch = useCallback(async (source, currentFilters, query) => {
    // Allow search with just a category OR a search query
    if (!query?.trim() && !currentFilters.category) {
      setListings([]);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setListings([]); // Clear previous results immediately
      
      const params = new URLSearchParams();
      if (query?.trim()) params.append('query', query);
      if (currentFilters.category) params.append('category', currentFilters.category);
      if (currentFilters.condition) params.append('condition', currentFilters.condition);
      if (currentFilters.minPrice) params.append('min_price', currentFilters.minPrice);
      if (currentFilters.maxPrice) params.append('max_price', currentFilters.maxPrice);
      params.append('sort', currentFilters.sort);
      params.append('source', source);
      
      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}/api/marketplace/public/search?${params.toString()}`;
      console.log('🔍 Fetching from:', apiUrl);
      
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error('Failed to fetch listings');
      }
      
      const data = await response.json();
      console.log('✅ Received listings:', data.listings?.length || 0);
      setListings(data.listings || []);
    } catch (err) {
      console.error('Failed to fetch public listings:', err);
      setError('Unable to fetch listings. Please try again.');
      setListings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle search submit
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim() || filters.category) {
      setSearchSubmitted(true);
      doFetch(activeSource, filters, searchQuery);
    }
  };

  // Handle refresh button
  const handleRefresh = () => {
    if (searchSubmitted || filters.category) {
      doFetch(activeSource, filters, searchQuery);
    }
  };

  // Handle source change - immediately fetch with new source
  const handleSourceChange = (newSource) => {
    setActiveSource(newSource);
    if (searchSubmitted || filters.category) {
      doFetch(newSource, filters, searchQuery);
    }
  };

  // Handle filter changes - re-fetch when filters change (if search was already submitted)
  useEffect(() => {
    const filtersChanged = JSON.stringify(filters) !== JSON.stringify(prevFiltersRef.current);
    
    if (filtersChanged && searchSubmitted) {
      prevFiltersRef.current = filters;
      doFetch(activeSource, filters, searchQuery);
    } else if (filters.category && !searchSubmitted) {
      // Auto-submit when category is selected without prior search
      setSearchSubmitted(true);
      prevFiltersRef.current = filters;
      doFetch(activeSource, filters, searchQuery);
    }
  }, [filters, activeSource, searchQuery, searchSubmitted, doFetch]);

  // Source tabs
  const sources = [
    { id: 'all', label: 'All Sources' },
    { id: 'reverb', label: 'Reverb' },
    { id: 'ebay', label: 'eBay' }
  ];

  return (
    <div className="mp-section" style={{ padding: '2rem', color: '#fff' }}>
      {/* Search Header */}
      <div style={{ marginBottom: '2rem' }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255, 255, 255, 0.5)' }} />
            <input
              type="text"
              placeholder="Search DJ gear (e.g., Pioneer CDJ-3000, Technics 1200)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 2.75rem',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(0, 255, 255, 0.3)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '0.95rem'
              }}
            />
          </div>
          <button
            type="submit"
            disabled={!searchQuery.trim() || loading}
            style={{
              padding: '0.75rem 1.25rem',
              background: loading ? 'rgba(0, 255, 255, 0.3)' : 'linear-gradient(135deg, #00ffff, #0080ff)',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
              whiteSpace: 'nowrap'
            }}
          >
            {loading ? <RefreshCw size={16} className="spin" /> : <Search size={16} />}
            {loading ? 'Searching...' : 'Search'}
          </button>
        </form>

        {/* Source Tabs */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', alignItems: 'center' }}>
          {sources.map((source) => {
            const isAllSources = source.id === 'all';
            const isActive = activeSource === source.id;
            const activeColor = isAllSources ? '#ff00ff' : '#00ffff';
            
            return (
              <button
                key={source.id}
                onClick={() => handleSourceChange(source.id)}
                style={{
                  padding: isAllSources ? '0.4rem 0.8rem' : '0.5rem 1rem',
                  background: isActive ? `${activeColor}33` : 'rgba(255, 255, 255, 0.05)',
                  border: isActive ? `2px solid ${activeColor}` : '2px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '20px',
                  color: isActive ? activeColor : 'rgba(255, 255, 255, 0.7)',
                  fontSize: isAllSources ? '0.8rem' : '0.9rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {source.label}
              </button>
            );
          })}
          
          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            disabled={loading}
            style={{
              marginLeft: 'auto',
              padding: '0.4rem 0.8rem',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.8rem',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem',
              transition: 'all 0.2s ease'
            }}
          >
            <RefreshCw size={12} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            Refresh
          </button>
        </div>

        {/* Filters Row */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <select
            value={filters.category}
            onChange={(e) => setFilters({...filters, category: e.target.value})}
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              color: '#fff',
              fontSize: '0.85rem',
              cursor: 'pointer'
            }}
          >
            <option value="" style={{ background: '#1e293b' }}>All Categories</option>
            <option value="dj" style={{ background: '#1e293b' }}>🎧 DJ & Lighting Gear</option>
            <option value="controllers" style={{ background: '#1e293b' }}>🎛️ DJ Controllers</option>
            <option value="turntables" style={{ background: '#1e293b' }}>🎵 Turntables</option>
            <option value="mixers" style={{ background: '#1e293b' }}>🎚️ Mixers</option>
            <option value="keyboards" style={{ background: '#1e293b' }}>🎹 Keyboards & Synths</option>
            <option value="synths" style={{ background: '#1e293b' }}>🎹 Synths</option>
            <option value="recording" style={{ background: '#1e293b' }}>🎙️ Recording Gear</option>
            <option value="drum-machines" style={{ background: '#1e293b' }}>🥁 Drum Machines</option>
            <option value="samplers" style={{ background: '#1e293b' }}>🔊 Samplers</option>
          </select>

          <select
            value={filters.condition}
            onChange={(e) => setFilters({...filters, condition: e.target.value})}
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              color: '#fff',
              fontSize: '0.85rem',
              cursor: 'pointer'
            }}
          >
            <option value="" style={{ background: '#1e293b' }}>Any Condition</option>
            <option value="new" style={{ background: '#1e293b' }}>New</option>
            <option value="used" style={{ background: '#1e293b' }}>Used</option>
          </select>

          <input
            type="number"
            placeholder="Min $"
            value={filters.minPrice}
            onChange={(e) => setFilters({...filters, minPrice: e.target.value})}
            style={{
              width: '100px',
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              color: '#fff',
              fontSize: '0.85rem'
            }}
          />

          <input
            type="number"
            placeholder="Max $"
            value={filters.maxPrice}
            onChange={(e) => setFilters({...filters, maxPrice: e.target.value})}
            style={{
              width: '100px',
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              color: '#fff',
              fontSize: '0.85rem'
            }}
          />

          <select
            value={filters.sort}
            onChange={(e) => setFilters({...filters, sort: e.target.value})}
            style={{
              padding: '0.5rem 1rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '20px',
              color: '#fff',
              fontSize: '0.85rem',
              cursor: 'pointer'
            }}
          >
            <option value="relevance" style={{ background: '#1e293b' }}>Most Relevant</option>
            <option value="price-low" style={{ background: '#1e293b' }}>Price: Low to High</option>
            <option value="price-high" style={{ background: '#1e293b' }}>Price: High to Low</option>
            <option value="newest" style={{ background: '#1e293b' }}>Newest First</option>
          </select>
        </div>
      </div>

      {/* Results Area */}
      {!searchSubmitted ? (
        /* Initial State - No search yet */
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem 2rem',
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '16px',
          border: '1px dashed rgba(0, 255, 255, 0.3)'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem', letterSpacing: '-0.15em' }}>🎚️🎛️🎚️</div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', color: '#fff' }}>
            Find Your New Gear
          </h3>
          <p style={{ color: 'rgba(255, 255, 255, 0.6)', maxWidth: '450px', margin: '0 auto 1.5rem', fontSize: '0.9rem' }}>
            Search across top marketplaces to find DJ gear, synths, and recording equipment from sellers worldwide.
          </p>
          <p style={{ color: '#00ffff', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
            👆 Enter a search term and search or select a category to get started
          </p>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '0.75rem',
            flexWrap: 'wrap'
          }}>
            <span style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8rem', alignSelf: 'center' }}>Try:</span>
            {['Pioneer CDJ-3000', 'Technics 1200', 'Allen & Heath', 'Moog Synthesizer'].map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => {
                  setSearchQuery(suggestion);
                  setSearchSubmitted(true);
                  doFetch(activeSource, filters, suggestion);
                }}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'rgba(0, 255, 255, 0.1)',
                  border: '1px solid rgba(0, 255, 255, 0.3)',
                  borderRadius: '20px',
                  color: '#00ffff',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      ) : loading ? (
        /* Loading State */
        <div style={{ textAlign: 'center', padding: '4rem' }}>
          <div className="spinner" style={{ 
            width: '48px', 
            height: '48px', 
            border: '4px solid rgba(0, 255, 255, 0.2)',
            borderTop: '4px solid #00ffff',
            borderRadius: '50%',
            margin: '0 auto 1rem',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ color: 'rgba(255, 255, 255, 0.6)' }}>Searching marketplaces...</p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      ) : error ? (
        /* Error State */
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem',
          background: 'rgba(255, 0, 0, 0.1)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 0, 0, 0.3)'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⚠️</div>
          <p style={{ color: '#ff6b6b' }}>{error}</p>
          <button
            onClick={handleRefresh}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1.5rem',
              background: 'rgba(0, 255, 255, 0.2)',
              border: '1px solid #00ffff',
              borderRadius: '8px',
              color: '#00ffff',
              cursor: 'pointer'
            }}
          >
            Try Again
          </button>
        </div>
      ) : listings.length === 0 ? (
        /* No Results State */
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem',
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '12px'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>🎧</div>
          <p style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            No listings found{searchQuery ? ` for "${searchQuery}"` : ' in this category'}. Try adjusting your search, filters, or source.
          </p>
          <button
            onClick={handleRefresh}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1.5rem',
              background: 'rgba(0, 255, 255, 0.2)',
              border: '1px solid #00ffff',
              borderRadius: '8px',
              color: '#00ffff',
              cursor: 'pointer'
            }}
          >
            Refresh Results
          </button>
        </div>
      ) : (
        /* Results Grid */
        <div>
          <div style={{ 
            marginBottom: '1rem', 
            fontSize: '0.9rem', 
            color: 'rgba(255, 255, 255, 0.6)' 
          }}>
            Found {listings.length} listings
          </div>
          <div className="mp-results-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '1.25rem'
          }}>
            {listings.map((listing) => (
              <PublicListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Public Listing Card Component
const PublicListingCard = ({ listing }) => {
  const [isHovered, setIsHovered] = useState(false);

  const sourceColors = {
    reverb: '#ff6b35',
    ebay: '#0064d2'
  };

  const sourceColor = sourceColors[listing.source] || '#00ffff';

  return (
    <a
      href={listing.url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ textDecoration: 'none' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        style={{
          background: isHovered 
            ? 'linear-gradient(135deg, rgba(0, 255, 255, 0.15), rgba(255, 0, 255, 0.15))'
            : 'rgba(255, 255, 255, 0.05)',
          borderRadius: '12px',
          border: isHovered ? `2px solid ${sourceColor}` : '2px solid rgba(255, 255, 255, 0.1)',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
          boxShadow: isHovered ? `0 10px 30px ${sourceColor}30` : 'none'
        }}
      >
        {/* Image */}
        <div style={{ 
          position: 'relative', 
          paddingTop: '75%', 
          background: 'rgba(0, 0, 0, 0.3)' 
        }}>
          {listing.image_url ? (
            <img
              src={listing.image_url}
              alt={listing.title}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255, 255, 255, 0.3)',
              fontSize: '3rem'
            }}>
              🎧
            </div>
          )}
          
          {/* Source Badge */}
          <div style={{
            position: 'absolute',
            top: '0.5rem',
            right: '0.5rem',
            padding: '0.25rem 0.6rem',
            background: sourceColor,
            borderRadius: '4px',
            fontSize: '0.7rem',
            fontWeight: '700',
            color: '#fff',
            textTransform: 'uppercase'
          }}>
            {listing.source}
          </div>

          {/* Condition Badge */}
          {listing.condition && (
            <div style={{
              position: 'absolute',
              top: '0.5rem',
              left: '0.5rem',
              padding: '0.25rem 0.6rem',
              background: listing.condition === 'new' ? 'rgba(0, 255, 0, 0.8)' : 'rgba(255, 165, 0, 0.8)',
              borderRadius: '4px',
              fontSize: '0.7rem',
              fontWeight: '600',
              color: '#fff'
            }}>
              {listing.condition}
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <h3 style={{
              fontSize: '0.95rem',
              fontWeight: '600',
              color: '#fff',
              margin: 0,
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {listing.title}
            </h3>
            <div onClick={(e) => e.preventDefault()}>
              <WishlistStar 
                itemType="gear"
                itemData={{
                  id: listing.id,
                  title: listing.title,
                  price: listing.price,
                  condition: listing.condition,
                  url: listing.url,
                  image_url: listing.image_url,
                  seller: listing.seller,
                  source: listing.source
                }}
                size={16}
              />
            </div>
          </div>

          {listing.seller && (
            <p style={{
              fontSize: '0.8rem',
              color: 'rgba(255, 255, 255, 0.5)',
              margin: '0 0 0.75rem 0'
            }}>
              by {listing.seller}
            </p>
          )}

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{
              fontSize: '1.25rem',
              fontWeight: '700',
              color: '#00ffff'
            }}>
              ${listing.price?.toLocaleString()}
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem',
              color: isHovered ? sourceColor : 'rgba(255, 255, 255, 0.5)',
              fontSize: '0.8rem',
              transition: 'color 0.2s'
            }}>
              <span>View</span>
              <ExternalLink size={14} />
            </div>
          </div>
        </div>
      </div>
    </a>
  );
};

export default PublicMarketplaceSection;
